import {
  contains,
  filter,
  length,
  merge,
  object_map,
  scan,
  to_array,
} from "@pdfking/rfunc"
import { Keys, SortBy, Values, Void } from "@pdfking/tfunc"
import { GENERIC_ERROR } from "@pdfking/translations/enum/errors"
import Icon from "@pdfking/workflow/components/Icon"
import fire_event from "@pdfking/workflow/utils/fire_event"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import truncate_chars from "@pdfking/workflow/utils/truncate_chars"
import uuid from "@pdfking/workflow/utils/uuid"
import delete_property from "@pdfking/workflow/vue/3/delete_property"
import { defineComponent, withModifiers } from "vue"
import closest from "../dom/closest"
import download_url from "../files/actions/download_url"
import upload_files from "../files/actions/upload_files"
import new_file_from_datatransfer from "../files/make/new_file_from_datatransfer"
import guess_extension from "../files/naming/guess_extension"
import guess_filecheck from "../files/naming/guess_filecheck"
import filenames from "../mixins/Filenames"
import storage from "../state/storage"
import user from "../state/user"
import TaskProgress from "./TaskProgress"

export default defineComponent({
  computed: merge(
    proxy(storage, { selected_files: "global_files" }),
    proxy(user, ["is_staff"]),

    {
      available_files() {
        // @ts-expect-error - Vue data prop
        const files = this.files
        return filter(
          (f: any) => !contains(files, f.path),
          // @ts-expect-error - Vue proxy prop
          this.global_files
        )
      },

      sorted_files() {
        // @ts-expect-error - Vue data prop
        return SortBy(Values(this.files), (f: any) => f.short_name)
      },
      translations: () => make_translations("actions"),
    }
  ),
  data() {
    return {
      files: {} as Record<string, any>,
      id: uuid(),
      loading: false,
      upload_task: null,
    }
  },
  emits: ["file-add", "file-change", "file-remove", "file-upload"] as const,
  methods: {
    add_available_files() {
      scan((f: any) => this.push_file(f), Values(this.available_files))
    },
    async create_file(e: any) {
      if (!this.multiple) this.files = {}

      const files = await Promise.all(
        to_array(e.dataTransfer ? e.dataTransfer.files : e.target.files).map(
          new_file_from_datatransfer
        )
      )

      scan(this.push_file, files)

      if (e.target) {
        e.target.value = null
      }
    },
    delete_available_files() {
      // @ts-expect-error - storage method
      storage.unselect_files(Keys(this.available_files))
    },
    delete_file(id?: string) {
      if (id) {
        const f = this.files[id]
        if (f) {
          delete_property(this.files, id)
          this.$emit("file-remove", f)

          // @ts-expect-error - storage method
          if (this.is_staff) storage.add_files([f])
        }
      } else {
        scan(this.delete_file, Keys(this.files))
      }
    },
    guess_extension,
    guess_filecheck,
    length,
    open_file(file: any) {
      file.get_url().then((u: string) => download_url(u, file.short_name))
    },
    push_file(file: any) {
      if (this.files[file.path]) {
        this.$emit("file-change", file)
      } else {
        this.$emit("file-add", file)
      }
      this.files[file.path] = file
    },
    truncate_chars,
    upload_files(files: any[]) {
      this.loading = true
      return upload_files(files).then(files => {
        this.$emit("file-upload", files)
        this.loading = false
        return files
      })
    },
  },
  mixins: [filenames],
  mounted() {
    this.files = object_map(
      (f: any) => f.path,
      (f: any) => f,
      this.filenames
    )

    let submitted = 0

    this.$nextTick(() => {
      if (this.field) {
        const form = closest(this.$el, "form")
        if (form) {
          form.addEventListener("submit", (event: Event) => {
            const local_files = filter(
              (f: any) => !f.file_meta.remote_id,
              Values(this.files)
            )

            if (length(local_files) > 0) {
              if (submitted >= 2) {
                throw GENERIC_ERROR
              }
              event.preventDefault()
              event.stopPropagation()
              this.upload_files(local_files).then(() =>
                fire_event(form, "submit")
              )
              submitted++
            }
          })
        }
      }
    })
  },
  props: ["field", "clear", "accept", "multiple", "filelist"] as const,
  render() {
    return (
      <label
        class={[
          "btn btn-outline-light text-start w-100 pe-2",
          {
            "bg-white": length(this.files) + length(this.available_files),
            "ps-2": length(this.files),
          },
        ]}
        for={this.id}
        onDrop={withModifiers((e: DragEvent) => this.create_file(e), ["prevent", "stop"])}
      >
        <div class="btn-toolbar">
          {this.sorted_files.map((file: any) => (
            <div key={file.path} class="input-group input-group-sm me-2">
              <a
                class={[
                  "btn p-0 px-1",
                  guess_filecheck(file.short_name)
                    ? "btn-success text-white"
                    : file.invalid
                      ? "btn-danger text-white"
                      : "btn-light",
                ]}
                download={file.short_name}
                href={file.url}
                target="_blank"
                title={file.short_name}
                onClick={withModifiers(() => this.open_file(file), ["prevent", "stop"])}
              >
                {truncate_chars(file.short_name)}
              </a>
              {file?.file_meta?.remote_name && (
                <input
                  name={this.field}
                  type="hidden"
                  value={file.file_meta.remote_name}
                />
              )}
              <button
                type="button"
                class={[
                  "btn p-0 px-1",
                  guess_filecheck(file.short_name)
                    ? "btn-success text-white"
                    : file.invalid
                      ? "btn-danger text-white"
                      : "btn-light",
                ]}
                disabled={this.upload_task}
                title={this.translations.actions.DELETE}
                onClick={withModifiers(() => this.delete_file(file.path), ["prevent", "stop"])}
              >
                ×
              </button>
            </div>
          ))}

          {length(this.files) == 0 && (
            <div>
              <Icon class="me-1" name="FILE" />
              {this.multiple ? (
                <span>{this.translations.actions.DROP_FILES}</span>
              ) : (
                <span>{this.translations.actions.DROP_FILE}</span>
              )}
              {!this.is_staff && (
                <span class="small fst-italic ms-1 text-muted">
                  {this.translations.actions.DROP_OR_CLICK}
                </span>
              )}
            </div>
          )}

          {this.loading && <TaskProgress />}

          {!this.loading && length(this.available_files) > 0 && (
            <div class="input-group input-group-sm ms-auto">
              <div
                class="btn btn-sm btn-outline-light p-0 px-1"
                onClick={withModifiers(() => this.add_available_files(), ["prevent", "stop"])}
              >
                {this.translations.actions.ADD}{" "}
                {`${length(this.available_files)} file`}
              </div>
              <div
                class="btn btn-sm btn-outline-light p-0 px-1"
                title={this.translations.actions.DELETE}
                onClick={withModifiers(() => this.delete_available_files(), ["prevent", "stop"])}
              >
                ×
              </div>
            </div>
          )}

          {!this.loading &&
            length(this.available_files) == 0 &&
            length(this.files) > 0 && (
              <div
                class="btn btn-outline-light p-0 px-1 border-0 ms-auto"
                title={this.translations.actions.DELETE}
                onClick={withModifiers(() => this.delete_file(), ["prevent", "stop"])}
              >
                ×
              </div>
            )}
        </div>

        <input
          class="d-none"
          id={this.id}
          ref="file"
          accept={this.accept}
          multiple={!!this.multiple}
          type="file"
          onChange={withModifiers((e: Event) => this.create_file(e), ["prevent"])}
        />
      </label>
    )
  },
})
